import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function AuthCallback() {
  const [params] = useSearchParams();
  const { completeLogin } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return; // React StrictMode / re-render guard: exchange the code exactly once
    started.current = true;
    const err = params.get("error");
    const code = params.get("code");
    const state = params.get("state");
    if (err) {
      setError(params.get("error_description") || "Sign-in was cancelled.");
      return;
    }
    if (!code || !state) {
      setError("Invalid sign-in response from Shopify.");
      return;
    }
    completeLogin(code, state)
      .then((customer) => {
        toast.success(`Welcome${customer?.first_name ? `, ${customer.first_name}` : ""}! You're signed in.`);
        navigate("/account", { replace: true });
      })
      .catch((e) => {
        const detail = e?.response?.data?.detail;
        setError(typeof detail === "string" ? detail : "Sign-in failed. Please try again.");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className="mx-auto max-w-md px-4 py-32 text-center" data-testid="auth-callback-page">
      {error ? (
        <>
          <h1 className="font-heading text-xl font-bold">Couldn't sign you in</h1>
          <p className="mt-2 text-sm text-neutral-500" data-testid="auth-callback-error">
            {error}
          </p>
          <Link
            to="/account"
            data-testid="auth-callback-retry"
            className="mt-6 inline-block rounded-full bg-neutral-900 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-neutral-700"
          >
            Back to Account
          </Link>
        </>
      ) : (
        <>
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-neutral-200 border-t-neutral-900" />
          <p className="mt-4 text-sm text-neutral-500">Completing secure sign-in with Shopify...</p>
          <p className="mt-2 flex items-center justify-center gap-1.5 text-xs text-neutral-400">
            <ShieldCheck size={13} /> Verifying your one-time code
          </p>
        </>
      )}
    </main>
  );
}
